import { Box, Card, CardContent, Typography } from "@mui/material";
import type { JSX } from "react";

import type { ProviderRole, ProviderRoles } from "../types.js";
import { providerLabel } from "./format.js";

/**
 * The real provider-per-task map. The provider switch above only changes CHAT;
 * embeddings + images are always OpenAI — this explains why OpenAI is billed
 * even when Claude is the active chat model. Extracted from `ProvidersPanel.tsx`.
 */
export function ProviderRolesCard({ roles }: { roles: ProviderRoles }): JSX.Element {
  const rows: { label: string; role: ProviderRole }[] = [
    { label: "Chat / completions", role: roles.chat },
    { label: "Embeddings (KB)", role: roles.embeddings },
    { label: "Images", role: roles.images },
  ];
  return (
    <Card>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          What each task uses
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
          The switch above only changes <Box component="strong">chat</Box>. Embeddings (every
          knowledge-base read/write) and image generation always run on OpenAI —
          so OpenAI is billed even when Claude is the active chat model.
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          {rows.map((r, i) => (
            <Box
              key={r.label}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                py: 1,
                ...(i > 0 ? { borderTop: 1, borderColor: "divider" } : {}),
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, color: "text.primary" }}>{r.label}</Typography>
                <Typography sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
                  {providerLabel(r.role.provider)} · {r.role.model}
                </Typography>
              </Box>
              <Box component="span" sx={{ flexShrink: 0, borderRadius: 0.5, bgcolor: "surfaceSoft", px: 0.75, py: 0.25, fontSize: 12, color: "text.disabled" }}>
                {r.role.switchable ? "switchable" : "fixed"}
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
